/**
 * Cockpit Ops page at /it/cockpit-ops.
 *
 * Read-only live view of every monitored scheduled job, from the api
 * `cockpit_ops_jobs` registry (`/jobs`) joined with the inverted-heartbeat
 * watcher's latest verdict per job (`/health`). One row per job: a one-line
 * description, cadence, last run, next run (computed), duration, enabled flag,
 * and a colored health pill. Clicking a row opens a modal that lists the job's
 * logic (detail_steps). No actions; every row is informational. The point is to
 * avoid SSHing into otto-it or opening CloudWatch to check operational state.
 *
 * Since the job-monitoring rebuild (spec job-monitoring-rebuild.md, 2026-08-25)
 * the new ops_job / ops_job_run tables FEED this same page: a job that reports
 * through the new start/finish path shows its real last run (started/finished),
 * outcome, duration, records handled, and the error text on failure, plus a
 * flagged "no interval set" state when its expected interval is unset. The
 * detail modal adds the job's recent run history. Still read-only:
 * editing the expected interval from here is a follow-up slice.
 *
 * Disabled jobs are hidden by default (a "Show disabled" toggle reveals them),
 * so the grid leads with what should currently be running.
 *
 * Back link top-left per .claude/rules/cockpit-ui-navigation.md.
 */

import { useMemo, useState } from 'react';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import {
  AlertTriangle,
  CheckCircle2,
  Cloud,
  Database,
  Info,
  Loader2,
  RefreshCw,
  Server,
  XCircle,
} from 'lucide-react';

import {
  formatDurationMs,
  formatLocalTime,
  monitorFacts,
  effectiveVerdict,
  type TriggerType,
  relativeTime,
  useCockpitOpsHealth,
  useCockpitOpsJobs,
  type HealthRow,
  type RegistryJob,
} from '@/lib/useCockpitOps';
import { Pill } from '@/components/Pill';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { JobStepsModal } from './CockpitOpsStepsModal';

/**
 * HealthPill sourced from the inverted-heartbeat watcher Lambda.
 *   ok       green   verdict_since shown in the tooltip
 *   late     amber   how long since last heartbeat is in detail
 *   failed   red     job errored in the last evaluation window
 *   unknown  gray    no verdict yet (new job, or watcher hasn't run)
 */
function HealthPill({ row }: { row: HealthRow | undefined }) {
  if (!row) {
    return (
      <Pill
        tone="bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted"
        title="No health verdict yet. The watcher Lambda may not have evaluated this job."
      >
        <AlertTriangle className="h-3 w-3" />
        unknown
      </Pill>
    );
  }
  const tone =
    row.verdict === 'ok'
      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
      : row.verdict === 'failed'
        ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
        : row.verdict === 'late'
          ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
          : 'bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted';
  const Icon =
    row.verdict === 'ok'
      ? CheckCircle2
      : row.verdict === 'failed'
        ? XCircle
        : AlertTriangle;
  const titleParts: string[] = [];
  if (row.detail) titleParts.push(row.detail);
  titleParts.push(`since ${formatLocalTime(row.verdict_since)}`);
  return (
    <Pill tone={tone} title={titleParts.join(' - ')}>
      <Icon className="h-3 w-3" />
      {row.verdict}
    </Pill>
  );
}

/**
 * LastResultPill: the newest completed run's outcome from the job-monitoring
 * tables. A failure also renders the error text, truncated to one line; the
 * full text is in the row's modal (click to expand).
 */
function LastResultPill({ job }: { job: RegistryJob }) {
  const facts = monitorFacts(job);
  if (facts.outcome === null) {
    // Only the new monitor reports both ends of a run, so only it can tell
    // that a job started and has not finished. A legacy row never carries a
    // finish time, and inferring 'running' from that would always be wrong.
    const inFlight = facts.isNew && facts.lastRunAt !== null && facts.lastFinishedAt === null;
    return (
      <Pill
        tone="bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted"
        title={
          inFlight
            ? `Started ${formatLocalTime(facts.lastRunAt as string)} and has not reported a finish yet.`
            : 'No completed run has been reported for this job yet.'
        }
      >
        {inFlight ? <Loader2 className="h-3 w-3" /> : <Info className="h-3 w-3" />}
        {inFlight ? 'running' : 'no runs yet'}
      </Pill>
    );
  }
  if (facts.outcome === 'success') {
    return (
      <Pill
        tone="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
        title={facts.lastFinishedAt ? `Finished ${formatLocalTime(facts.lastFinishedAt)}` : undefined}
      >
        <CheckCircle2 className="h-3 w-3" />
        success
      </Pill>
    );
  }
  return (
    <div className="min-w-0">
      <Pill
        tone="bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"
        title={facts.lastFinishedAt ? `Failed ${formatLocalTime(facts.lastFinishedAt)}` : undefined}
      >
        <XCircle className="h-3 w-3" />
        failure
      </Pill>
      {facts.errorText ? (
        <div
          className="mt-1 max-w-[220px] truncate font-mono text-[11px] text-red-700 dark:text-red-300"
          title="Click the row for the full error text"
        >
          {facts.errorText}
        </div>
      ) : null}
    </div>
  );
}

const TRIGGER_LABEL: Record<TriggerType, string> = {
  schedule: 'Scheduled',
  event: 'Event',
  manual: 'Manual',
};

const TRIGGER_TITLE: Record<TriggerType, string> = {
  schedule: 'Runs on a schedule, so the watcher expects it every interval.',
  event: 'Runs when something happens (an upload, a message), so it has no interval.',
  manual: 'Run by hand when needed, so it has no interval.',
};

function TriggerPill({ type }: { type: TriggerType }) {
  return (
    <Pill
      tone="bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted"
      title={TRIGGER_TITLE[type]}
    >
      {TRIGGER_LABEL[type]}
    </Pill>
  );
}

function cadenceLabel(minutes: number): string {
  if (minutes < 60) return `Every ${minutes}m`;
  if (minutes === 60) return 'Hourly';
  if (minutes % 1440 === 0) return `Daily (${minutes / 1440}d)`;
  if (minutes % 60 === 0) return `Every ${minutes / 60}h`;
  return `Every ${minutes}m`;
}

function JobRow({
  job,
  health,
  onOpen,
}: {
  job: RegistryJob;
  health: HealthRow | undefined;
  onOpen: (job: RegistryJob) => void;
}) {
  const hasSteps = job.detail_steps.length > 0;
  const facts = monitorFacts(job);
  // Tooltip for the last-run cell: the new system knows both ends of the run.
  const lastRunTitle = facts.lastRunAt
    ? facts.isNew
      ? `Started ${formatLocalTime(facts.lastRunAt)}${
          facts.lastFinishedAt ? `, finished ${formatLocalTime(facts.lastFinishedAt)}` : ', still running'
        }`
      : formatLocalTime(facts.lastRunAt)
    : undefined;
  return (
    <tr
      className="border-b border-amp-line last:border-0 dark:border-amp-dark-line cursor-pointer hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm"
      onClick={() => onOpen(job)}
      title="Click for the job's logic"
    >
      <td className="py-2 px-3 align-top">
        <div className="flex items-start gap-2">
          <Info
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted"
            aria-hidden
          />
          <div className="min-w-0">
            <div className="font-mono text-xs text-amp-ink dark:text-amp-dark-ink break-all">
              {job.job_id}
            </div>
            {job.short_description ? (
              <div className="mt-0.5 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                {job.short_description}
              </div>
            ) : !hasSteps ? (
              <div className="mt-0.5 text-xs italic text-amp-ink-muted dark:text-amp-dark-ink-muted">
                No description on file.
              </div>
            ) : null}
          </div>
        </div>
      </td>
      <td className="py-2 px-3 text-xs align-top">
        <Pill className="bg-amp-cream-warm text-amp-ink-muted border-amp-cream-warm dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted dark:border-transparent">
          {job.source === 'lambda' ? (
            <Cloud className="h-3 w-3" />
          ) : (
            <Server className="h-3 w-3" />
          )}
          {job.source}
        </Pill>
      </td>
      <td className="py-2 px-3 text-xs align-top">
        <TriggerPill type={facts.triggerType} />
      </td>
      <td className="py-2 px-3 text-xs text-amp-ink dark:text-amp-dark-ink align-top whitespace-nowrap">
        {facts.triggerType !== 'schedule' ? (
          <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
            n/a ({facts.triggerType})
          </span>
        ) : facts.isNew && facts.intervalUnset ? (
          <Pill
            tone="bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
            title="No expected interval is set for this job, so the watcher cannot tell when it is late. Set one from Cockpit (edit UI is a follow-up)."
          >
            <AlertTriangle className="h-3 w-3" />
            no interval set
          </Pill>
        ) : (
          cadenceLabel(facts.cadenceMinutes ?? job.cadence_minutes)
        )}
      </td>
      <td className="py-2 px-3 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted align-top whitespace-nowrap">
        {facts.lastRunAt ? (
          <span title={lastRunTitle}>{relativeTime(facts.lastRunAt)}</span>
        ) : (
          <span className="italic">-</span>
        )}
      </td>
      <td className="py-2 px-3 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted align-top whitespace-nowrap">
        {job.next_run ? (
          <span title={formatLocalTime(job.next_run)}>{relativeTime(job.next_run)}</span>
        ) : (
          <span className="italic">-</span>
        )}
      </td>
      <td className="py-2 px-3 text-xs text-amp-ink dark:text-amp-dark-ink align-top whitespace-nowrap tabular-nums">
        {formatDurationMs(facts.durationMs)}
      </td>
      <td className="py-2 px-3 text-xs text-amp-ink dark:text-amp-dark-ink align-top whitespace-nowrap tabular-nums">
        {facts.records !== null ? facts.records.toLocaleString() : <span className="italic text-amp-ink-muted dark:text-amp-dark-ink-muted">-</span>}
      </td>
      <td className="py-2 px-3 text-xs align-top">
        <LastResultPill job={job} />
      </td>
      <td className="py-2 px-3 text-xs align-top">
        {job.enabled ? (
          <Pill tone="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
            <CheckCircle2 className="h-3 w-3" /> enabled
          </Pill>
        ) : (
          <Pill tone="bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted">
            <XCircle className="h-3 w-3" /> disabled
          </Pill>
        )}
      </td>
      <td className="py-2 px-3 text-xs align-top">
        <HealthPill
          row={
            health
              ? { ...health, verdict: effectiveVerdict(job, health.verdict) }
              : undefined
          }
        />
      </td>
    </tr>
  );
}

export function CockpitOps() {
  const { jobs, isLoading, isFetching, error, refetch } = useCockpitOpsJobs();
  const { health, fetchedAt, refetch: refetchHealth } = useCockpitOpsHealth();

  const [showDisabled, setShowDisabled] = useState(false);
  const [openJob, setOpenJob] = useState<RegistryJob | null>(null);

  const visibleJobs = useMemo(
    () => (showDisabled ? jobs : jobs.filter((j) => Boolean(j.enabled))),
    [jobs, showDisabled],
  );
  const hiddenCount = useMemo(
    () => jobs.filter((j) => !j.enabled).length,
    [jobs],
  );

  const columns = [
    'job',
    'source',
    'trigger',
    'cadence',
    'last run',
    'next run',
    'duration',
    'records',
    'last result',
    'status',
    'health',
  ];

  const toolbar = (
    <div className="flex flex-wrap items-center gap-3 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={showDisabled}
          onChange={(e) => setShowDisabled(e.target.checked)}
          className="accent-amp-olive dark:accent-amp-olive-dark"
        />
        Show disabled{hiddenCount > 0 ? ` (${hiddenCount})` : ''}
      </label>
      <span>
        Health refreshed:{' '}
        {fetchedAt ? (
          <span title={formatLocalTime(fetchedAt)}>{relativeTime(fetchedAt)}</span>
        ) : (
          'never'
        )}
      </span>
      <button
        type="button"
        onClick={() => {
          refetch();
          refetchHealth();
        }}
        disabled={isFetching}
        className="inline-flex items-center gap-1 border border-amp-line text-amp-ink rounded px-2 py-1 hover:bg-amp-cream-warm disabled:opacity-50 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
      >
        <RefreshCw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} />
        Refresh
      </button>
    </div>
  );

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            IT
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">
            Cockpit Ops
          </h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Monitored scheduled jobs (otto-it tasks + AWS Lambdas) with the latest
            health verdict from the inverted-heartbeat watcher. Click a job for its
            logic.
          </p>
        </div>
        <ToolPinToggle toolSlug="cockpit-ops" toolName="Cockpit Ops" />
      </div>

      <DataGridShell
        title={
          <span className="inline-flex items-center gap-2">
            <Database className="h-4 w-4 text-amp-olive dark:text-amp-olive-dark" />
            Monitored jobs
          </span>
        }
        loaded={isLoading ? undefined : visibleJobs.length}
        countNoun="jobs"
        toolbar={toolbar}
      >
        <table className="min-w-full">
          <thead className={gridTheadClassName}>
            <tr>
              {columns.map((h) => (
                <th key={h} className="py-2 px-3 text-left text-xs font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-6 text-center text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  <Loader2 className="inline h-4 w-4 animate-spin mr-2" />
                  Loading jobs...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-6 text-center text-red-700 dark:text-red-300">
                  Could not load jobs: {error.message}
                </td>
              </tr>
            ) : visibleJobs.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-6 text-center text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  {jobs.length === 0
                    ? 'No monitored jobs found.'
                    : 'No enabled jobs. Toggle "Show disabled" to see paused jobs.'}
                </td>
              </tr>
            ) : (
              visibleJobs.map((job) => (
                <JobRow
                  key={job.job_id}
                  job={job}
                  health={health.get(job.job_id)}
                  onOpen={setOpenJob}
                />
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>

      {openJob && (
        <JobStepsModal job={openJob} onClose={() => setOpenJob(null)} />
      )}
    </div>
  );
}
